/* eslint-disable @typescript-eslint/no-explicit-any */
import {
  Component,
  EventEmitter,
  Input,
  OnChanges,
  Output,
  SimpleChanges,
  TemplateRef,
} from '@angular/core';
import { Nullable } from '../../../types';
import { PTableHeader } from './p-table-header';

@Component({
  selector: 'p-table',
  templateUrl: './p-table.component.html',
  styleUrls: ['./p-table.component.scss'],
})
export class PTableComponent<T extends Record<string, any>>
  implements OnChanges
{
  /**
   * @Input() headers
   * Tableau d'en-têtes de colonnes pour le composant de la table. Chaque en-tête contient un label, une clé,
   * et éventuellement des informations supplémentaires comme la largeur de la colonne ou si elle est triable.
   * @type {PTableHeader[]}
   * @default []
   */
  @Input() headers: PTableHeader[] = [];

  /**
   * @Input() items
   * Tableau d'éléments de données qui seront affichés dans le corps de la table. Chaque élément correspond
   * à une ligne de la table et doit respecter la structure définie par les en-têtes.
   * @type {T[]}
   * @default []
   */
  @Input() items: T[] = [];

  /**
   * @Input() rowTemplate
   * Gabarit personnalisé pour afficher les lignes de la table. Il permet de personnaliser l'apparence
   * et le contenu des lignes en fonction des données.
   * @type {Nullable<TemplateRef<any>>}
   * @default null
   */
  @Input() rowTemplate: Nullable<TemplateRef<any>> = null;

  /**
   * @Input() sortColumn
   * Nom de la colonne actuellement triée. Si aucune colonne n'est triée, la valeur est `null`.
   * Cette valeur est utilisée pour indiquer quelle colonne doit afficher l'état de tri (ascendant ou descendant).
   * @type {string | null}
   * @default null
   */
  @Input() sortColumn: string | null = null;

  /**
   * @Input() sortDirection
   * Direction du tri pour la colonne spécifiée dans `sortColumn`. Les valeurs possibles sont :
   * - `'asc'` : tri ascendant
   * - `'desc'` : tri descendant
   * - `null` : pas de tri actif
   * @type {'asc' | 'desc' | null}
   * @default null
   */
  @Input() sortDirection: 'asc' | 'desc' | null = null;

  /**
   * @Output() sortChanged
   * Événement émis lorsque l'utilisateur change le tri d'une colonne. Cet événement transmet un objet contenant :
   * - `key` : la clé de la colonne triée ou `null` si aucune colonne n'est triée
   * - `direction` : la direction du tri (ascendant, descendant ou neutre)
   * @type {EventEmitter<{ key: string | null; direction: 'asc' | 'desc' | null; }>}
   */
  @Output() sortChanged = new EventEmitter<{
    key: string | null;
    direction: 'asc' | 'desc' | null;
  }>();

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['sortColumn'] || changes['sortDirection']) {
      this.updateSortArrow();
    }
  }

  onSortColumn(header: PTableHeader): void {
    if (!header.sortable) return;

    if (this.sortColumn !== header.key) {
      this.sortColumn = header.key;
      this.sortDirection = 'asc';
    } else {
      this.sortDirection =
        this.sortDirection === 'asc'
          ? 'desc'
          : this.sortDirection === 'desc'
            ? null
            : 'asc';
    }

    this.sortChanged.emit({
      key: this.sortDirection ? this.sortColumn : null,
      direction: this.sortDirection,
    });
  }

  isNeutral(header: PTableHeader): boolean {
    return this.sortColumn !== header.key || this.sortDirection === null;
  }

  // Logique pour s'assurer que la flèche est correctement affichée à l'initialisation
  private updateSortArrow(): void {
    if (this.sortColumn && this.sortDirection) {
      this.sortChanged.emit({
        key: this.sortColumn,
        direction: this.sortDirection,
      });
    }
  }
}
